import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ShutdownDstAppService } from './shutdown-dst-app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-shutdown-dst-app',
    templateUrl: './shutdown-dst-app.component.html',
    styleUrls: ['./shutdown-dst-app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ShutdownDstAppComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  isLocked: string = '1';
  savingLock = false;
  GENERAL_CONFIG: { base_url?: string } = {};
  globalService: any = {};
  filterParams: any = {};
  private isInitialized = false;
  private isLoading = false;

  constructor(
    private shutdownDstAppService: ShutdownDstAppService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
    this.tryInit();
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.baseUrl) return;
    this.isInitialized = true;
    this.loadStatus();
  }

  loadStatus(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.shutdownDstAppService.getSystemLockUnlockStatus(this.baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response?.callSuccess === '1' && response?.jsonObject?.length) {
          this.isLocked = response.jsonObject[0].SYSTEMLOCK ?? '1';
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading DST system lock status:', err);
        this.cdr.detectChanges();
      },
    });
  }

  saveLockUnlock(): void {
    if (this.savingLock) return;
    this.savingLock = true;
    const lockSystem = this.isLocked === '0' ? '1' : '0';
    this.cdr.detectChanges();

    this.shutdownDstAppService
      .saveSystemLockUnlock(this.baseUrl, lockSystem, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.savingLock = false;
          if (response?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              'System ' + (lockSystem === '1' ? 'Locked' : 'Unlocked') + ' Successfully!',
              'gtw-snackbar--green'
            );
            this.isLocked = lockSystem;
          } else {
            this.gtwSnackbarService.openSnackBar(
              response?.errorMessage || 'Something went wrong, please try again!',
              'gtw-snackbar--red'
            );
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.savingLock = false;
          this.gtwSnackbarService.openSnackBar(
            'Something went wrong, please try again!',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        },
      });
  }
}
